/**
 * KYMEE — Dawn Parity Facets Controller (iOS/Android/Desktop SAFE)
 * Works with your facets.liquid + price-facet markup:
 * - #FacetFiltersForm, #FacetFiltersFormMobile
 * - .active-facets-desktop / .active-facets-mobile
 * - #ProductGridContainer
 * - #mob_sort_by_overlay + .facet-sort-option
 *
 * Emits events after update:
 * - collection:updated
 * - kymee:collection:reset
 */

(() => {
    "use strict";

    /* -----------------------------
      Small helpers
    ----------------------------- */
    const safe = (fn) => {
        try { return fn(); } catch (e) { console.error("[KYMEE Facets]", e); }
    };

    const debounce = (fn, delay = 250) => {
        let t;
        return (...args) => {
            clearTimeout(t);
            t = setTimeout(() => fn(...args), delay);
        };
    };

    // iOS safe requestIdleCallback polyfill
    if (!("requestIdleCallback" in window)) {
        window.requestIdleCallback = (cb) =>
            setTimeout(() => cb({ didTimeout: false, timeRemaining: () => 16 }), 1);
        window.cancelIdleCallback = (id) => clearTimeout(id);
    }

    const raf2 = (fn) => requestAnimationFrame(() => requestAnimationFrame(fn));

    /* -----------------------------
      Selectors (your markup)
    ----------------------------- */
    const SEL = {
        gridContainer: "#ProductGridContainer",
        desktopForm: "#FacetFiltersForm",
        mobileForm: "#FacetFiltersFormMobile",
        activeDesktop: ".active-facets-desktop",
        activeMobile: ".active-facets-mobile",
        count: "#ProductCount",
        countDesktop: "#ProductCountDesktop",
        spinner: ".loading__spinner",

        // Mobile drawer in your snippet:
        mobileDrawerDetails: ".mobile-facets__disclosure", // <details> wrapper
        mobileInner: ".mobile-facets__inner",

        // Facet details open state:
        desktopDetails: ".facets__wrapper details.js-filter, details.facets__disclosure-vertical.js-filter, details.facets__disclosure.js-filter",
        mobileDetails: ".mobile-facets__details.js-filter",

        // Sort overlay
        sortOverlay: "#mob_sort_by_overlay",
        sortOption: ".facet-sort-option",
        sortSelect: "select[name='sort_by']",
        sortOpenButton: ".mobile-facets__sort",
        sortCloseButton: "#close-sort-by",
    };

    /* -----------------------------
      State store (single source of truth)
    ----------------------------- */
    const STATE = {
        // URL params currently applied
        params: window.location.search.replace(/^\?/, ""),

        // which details are open (keys are element IDs)
        openDesktop: new Set(),
        openMobile: new Set(),

        // mobile drawer open?
        mobileDrawerOpen: false,

        // request mgmt
        abort: null,
        reqId: 0,

        // cache
        cache: new Map(), // url -> html
        cacheLimit: 15,
    };

    const cacheSet = (k, v) => {
        STATE.cache.set(k, v);
        if (STATE.cache.size > STATE.cacheLimit) {
            // delete oldest
            const firstKey = STATE.cache.keys().next().value;
            STATE.cache.delete(firstKey);
        }
    };

    /* -----------------------------
      Utilities: read/restore open state
    ----------------------------- */
    const keyOf = (el) => el?.id || el?.getAttribute?.("data-index") || null;

    const captureOpenState = () => {
        STATE.openDesktop.clear();
        STATE.openMobile.clear();

        document.querySelectorAll(SEL.desktopDetails).forEach((d) => {
            const k = keyOf(d);
            if (k && d.open) STATE.openDesktop.add(k);
        });

        document.querySelectorAll(SEL.mobileDetails).forEach((d) => {
            const k = keyOf(d);
            if (k && d.open) STATE.openMobile.add(k);
        });

        const drawer = document.querySelector(SEL.mobileDrawerDetails);
        STATE.mobileDrawerOpen = !!drawer?.open;
    };

    const restoreOpenState = () => {
        document.querySelectorAll(SEL.desktopDetails).forEach((d) => {
            const k = keyOf(d);
            if (!k) return;
            if (STATE.openDesktop.has(k)) d.open = true;
        });

        document.querySelectorAll(SEL.mobileDetails).forEach((d) => {
            const k = keyOf(d);
            if (!k) return;
            if (STATE.openMobile.has(k)) d.open = true;
        });

        const drawer = document.querySelector(SEL.mobileDrawerDetails);
        if (drawer && STATE.mobileDrawerOpen) {
            drawer.open = true;
            // lock scroll (Dawn does via body class/overflow; we keep minimal)
            //   document.body.classList.add("overflow-hidden");
        }
    };

    /* -----------------------------
      Sections resolver (Dawn style)
    ----------------------------- */
    const getSections = () => {
        const sections = [];

        const gridSection = document.getElementById("product-grid")?.dataset?.id;
        if (gridSection) sections.push(gridSection);

        const filtersSection =
            document.querySelector("#main-collection-filters")?.dataset?.id ||
            document.querySelector("[data-facets-section-id]")?.getAttribute("data-facets-section-id") ||
            document.querySelector(".facets-container")?.closest("[data-section-id]")?.getAttribute("data-section-id") ||
            document.querySelector("facet-filters-form")?.closest("[data-section-id]")?.getAttribute("data-section-id");

        if (filtersSection && filtersSection !== gridSection) sections.push(filtersSection);

        // unique
        return [...new Set(sections)].filter(Boolean);
    };

    /* -----------------------------
      DOM patch (only what we need)
    ----------------------------- */
    const patchHTML = (doc) => {
        // Grid
        const freshGrid = doc.querySelector(SEL.gridContainer);
        const curGrid = document.querySelector(SEL.gridContainer);
        if (freshGrid && curGrid) curGrid.innerHTML = freshGrid.innerHTML;

        // Desktop facets form
        const freshDesktop = doc.querySelector(SEL.desktopForm);
        const curDesktop = document.querySelector(SEL.desktopForm);
        if (freshDesktop && curDesktop) curDesktop.innerHTML = freshDesktop.innerHTML;

        // Mobile facets form
        const freshMobile = doc.querySelector(SEL.mobileForm);
        const curMobile = document.querySelector(SEL.mobileForm);
        if (freshMobile && curMobile) curMobile.innerHTML = freshMobile.innerHTML;

        // Kymee filter bar panel form
        // Kymee filter bar panel form
        // Important: do NOT replace KFB form while the mobile panel is open.
        // Otherwise taps/selected filters can be lost during AJAX render.
        const freshKFP = doc.querySelector("#kfbPanelForm");
        const curKFP = document.querySelector("#kfbPanelForm");
        const kfbWrap = document.getElementById("kfbWrap");
        const kfbPanelOpen = !!kfbWrap?.classList.contains("is-open");

        if (freshKFP && curKFP && !kfbPanelOpen) {
            curKFP.innerHTML = freshKFP.innerHTML;
        }

        // Active facets + counts (if present)
        [SEL.activeDesktop, SEL.activeMobile, SEL.count, SEL.countDesktop].forEach((sel) => {
            const f = doc.querySelector(sel);
            const c = document.querySelector(sel);
            if (f && c) c.innerHTML = f.innerHTML;
        });

    };

    const setLoading = (on) => {
        const grid = document.querySelector(SEL.gridContainer);
        if (on) grid?.classList.add("loading");
        else grid?.classList.remove("loading");

        document.querySelectorAll(SEL.spinner).forEach((s) => {
            if (on) s.classList.remove("hidden");
            else s.classList.add("hidden");
        });
    };

    /* -----------------------------
      URL + form params builder
    ----------------------------- */
    const serializeForm = (form) => {
        if (!form) return "";
        const fd = new FormData(form);
        return new URLSearchParams(fd).toString();
    };

    const collectParams = () => {
        // merge forms — skip any facet-filters-form marked data-kfp-skip (mobile drawer duplicate)
        // on mobile, also skip forms inside .facets-container (desktop form, hidden but still in DOM)
        const isMobile = window.matchMedia("(max-width: 749px)").matches;
        const forms = Array.from(document.querySelectorAll("facet-filters-form:not([data-kfp-skip]) form"))
            .filter(form => !(isMobile && form.closest(".facets-container")));
        const parts = forms.map(serializeForm).filter(Boolean);
        let params = parts.join("&");

        // On mobile the KFB panel has no sort_by input — carry it over from the current URL
        // so that applying a filter chip doesn't silently drop the active sort order.
        if (isMobile) {
            const sp = new URLSearchParams(params);
            if (!sp.has("sort_by")) {
                const curSort = new URLSearchParams(window.location.search).get("sort_by");
                if (curSort) { sp.set("sort_by", curSort); params = sp.toString(); }
            }
        }


        return params;
    };

    const updateURL = (params) => {
        const url = `${location.pathname}${params ? "?" + params : ""}`;
        history.pushState({ searchParams: params }, "", url);
    };

    /* -----------------------------
      Fetch pipeline (abort + dedupe + cache)
    ----------------------------- */
    const fetchSection = async (sectionId, params, signal) => {
        const url = `${location.pathname}?section_id=${sectionId}${params ? "&" + params : ""}`;

        if (STATE.cache.has(url)) return STATE.cache.get(url);

        const res = await fetch(url, {
            signal,
            headers: { "X-Requested-With": "XMLHttpRequest" },
            credentials: "same-origin",
        });
        const html = await res.text();
        cacheSet(url, html);
        return html;
    };

    const render = async (
        params,
        { updateHistory = true, restoreDropdowns = false, force = false } = {}
    ) => {
        params = (params || "").trim();

        // If same params, don't silently fail. Still fire completed event.
        if (!force && params === STATE.params && updateHistory) {
            setLoading(false);
            document.dispatchEvent(
                new CustomEvent("collection:updated", {
                    detail: { source: "facets", skipped: true }
                })
            );
            return false;
        }

        captureOpenState();

        if (STATE.abort) safe(() => STATE.abort.abort());
        STATE.abort = new AbortController();

        const signal = STATE.abort.signal;
        const myReq = ++STATE.reqId;

        STATE.params = params;

        // Important: update URL immediately so user feels Apply worked instantly.
        if (updateHistory) updateURL(params);

        setLoading(true);

        const sections = getSections();

        if (!sections.length) {
            setLoading(false);
            return false;
        }

        try {
            const htmlList = await Promise.all(
                sections.map((sid) => fetchSection(sid, params, signal))
            );

            if (myReq !== STATE.reqId) return false;

            htmlList.forEach((html) => {
                const doc = new DOMParser().parseFromString(html, "text/html");
                patchHTML(doc);
            });

            if (restoreDropdowns) {
                restoreOpenState();
            } else {
                const drawer = document.querySelector(SEL.mobileDrawerDetails);
                if (drawer && STATE.mobileDrawerOpen) drawer.open = true;
            }

            raf2(() => {
                document.body.offsetHeight;

                raf2(() => {
                    setLoading(false);

                    safe(() => {
                        const ff = document.querySelector("facet-filters-form");
                        ff && customElements.upgrade(ff);

                        document.querySelectorAll("price-range, facet-remove").forEach((el) => {
                            try {
                                customElements.upgrade(el);
                            } catch (_) { }
                        });
                    });

                    document.dispatchEvent(
                        new CustomEvent("collection:updated", {
                            detail: { source: "facets" }
                        })
                    );

                    document.dispatchEvent(
                        new CustomEvent("kymee:collection:reset", {
                            detail: { source: "facets" }
                        })
                    );
                });
            });

            return true;
        } catch (e) {
            if (e?.name === "AbortError") return false;

            console.error("[KYMEE Facets] render error:", e);
            setLoading(false);

            document.dispatchEvent(
                new CustomEvent("collection:updated", {
                    detail: { source: "facets", error: true }
                })
            );

            return false;
        }
    };

    /* -----------------------------
      FacetFiltersForm element (Dawn-like)
    ----------------------------- */
    class FacetFiltersForm extends HTMLElement {
        connectedCallback() {
            if (this.__bound) return;
            this.__bound = true;

            const form = this.querySelector("form");
            if (!form) return;

            // KFB mobile panel should NOT auto-render.
            // It should apply only on Apply / Clear / Sort button.
            if (form.id === "kfbPanelForm") return;

            const onChange = debounce((ev) => {
                // Price slider has its own controlled logic.
                // Do not let normal form listener fire AJAX while dragging.
                if (
                    ev.target.closest(".price-range-slider") ||
                    ev.target.closest(".price-range-inputs")
                ) {
                    return;
                }

                safe(() => ev?.preventDefault?.());
                render(collectParams(), { updateHistory: true });
            }, 180);

            form.addEventListener("change", onChange);
        }

        onActiveFilterClick(params, options = {}) {
            return render((params || "").trim(), {
                updateHistory: true,
                force: !!options.force
            });
        }
    }

    class FacetRemove extends HTMLElement {
        connectedCallback() {
            if (this.__bound) return;
            this.__bound = true;

            const link = this.querySelector("a");
            if (!link) return;

            link.setAttribute("role", "button");

            const run = (e) => {
                safe(() => e.preventDefault());
                const params = (link.href.split("?")[1] || "").trim();
                const ff = document.querySelector("facet-filters-form");
                ff?.onActiveFilterClick?.(params);
            };

            link.addEventListener("click", run);
            link.addEventListener("keyup", (e) => {
                if (e.key === " " || e.code === "Space") run(e);
            });
        }
    }

    /* -----------------------------
      Price Range Slider
      Selector: div.price-range-slider (avoids matching <price-range> wrapper)
      Parent must contain .price-range-inputs with -GTE / -LTE inputs
      Calls render() directly — no reliance on form event chain
    ----------------------------- */
    const initPriceSliders = () => {
        document.querySelectorAll('div.price-range-slider:not([data-price-init])').forEach(slider => {
            slider.setAttribute('data-price-init', '1');

            const rangeMin = slider.querySelector('.price-range-slider__input--min');
            const rangeMax = slider.querySelector('.price-range-slider__input--max');
            const trackActive = slider.querySelector('.price-range-slider__track-active');

            if (!rangeMin || !rangeMax || !trackActive) return;

            const parent = slider.parentElement;
            const inputsWrapper = parent && parent.querySelector('.price-range-inputs');
            const textMin = inputsWrapper && inputsWrapper.querySelector('input[id$="-GTE"]');
            const textMax = inputsWrapper && inputsWrapper.querySelector('input[id$="-LTE"]');
            const displayMin = inputsWrapper && inputsWrapper.querySelector('[data-price-display="min"]');
            const displayMax = inputsWrapper && inputsWrapper.querySelector('[data-price-display="max"]');

            if (!textMin || !textMax) return;

            const isKFB = !!slider.closest('#kfbPanelForm');

            const maxCap = () => {
                const v = parseInt(rangeMax.getAttribute('max') || '0', 10);
                return Number.isFinite(v) && v > 0 ? v : 1;
            };

            const cleanNumber = (value, fallback) => {
                const n = parseInt(String(value || '').replace(/,/g, ''), 10);
                return Number.isFinite(n) ? n : fallback;
            };

            const updateTrack = (min, max) => {
                const cap = maxCap();
                const left = (min / cap) * 100;
                const right = 100 - ((max / cap) * 100);

                trackActive.style.left = `${left}%`;
                trackActive.style.right = `${right}%`;

                // Better thumb touch priority when handles come close
                rangeMin.style.zIndex = min >= max ? '5' : '3';
                rangeMax.style.zIndex = '4';
            };

            const setValues = (min, max, activeInput = null) => {
                const cap = maxCap();

                min = Math.max(0, Math.min(cleanNumber(min, 0), cap));
                max = Math.max(0, Math.min(cleanNumber(max, cap), cap));

                // Do not let both handles fight each other
                if (activeInput === rangeMin && min > max) min = max;
                if (activeInput === rangeMax && max < min) max = min;

                if (!activeInput && max < min) max = min;

                rangeMin.value = String(min);
                rangeMax.value = String(max);
                textMin.value = String(min);
                textMax.value = String(max);

                if (displayMin) displayMin.textContent = String(min);
                if (displayMax) displayMax.textContent = String(max);

                updateTrack(min, max);
            };

            const triggerRender = () => {
                if (isKFB) {
                    safe(() => {
                        if (window.kymeePreviewKfbCount) {
                            window.kymeePreviewKfbCount();
                        }
                    });
                    return;
                }

                safe(() => render(collectParams(), { updateHistory: true }));
            };

            const onRangeInput = (e) => {
                e.preventDefault?.();
                e.stopPropagation();

                setValues(rangeMin.value, rangeMax.value, e.target);
            };

            const onRangeChange = (e) => {
                e.preventDefault?.();
                e.stopPropagation();

                setValues(rangeMin.value, rangeMax.value, e.target);
                triggerRender();
            };

            const onHiddenChange = (e) => {
                e.preventDefault?.();
                e.stopPropagation();

                setValues(textMin.value, textMax.value);
                triggerRender();
            };

            const stop = (e) => e.stopPropagation();

            rangeMin.addEventListener('input', onRangeInput, { passive: false });
            rangeMax.addEventListener('input', onRangeInput, { passive: false });

            rangeMin.addEventListener('change', onRangeChange);
            rangeMax.addEventListener('change', onRangeChange);

            textMin.addEventListener('input', stop);
            textMax.addEventListener('input', stop);

            textMin.addEventListener('change', onHiddenChange);
            textMax.addEventListener('change', onHiddenChange);

            setValues(rangeMin.value, rangeMax.value);
        });
    };

    /* -----------------------------
      Global delegated clicks (chips + clear-all)
    ----------------------------- */
    const bindDelegatesOnce = () => {
        if (window.__kymeeFacetsBound) return;
        window.__kymeeFacetsBound = true;

        document.addEventListener("click", (e) => {
            // chip remove + clear all (desktop + mobile)
            const a = e.target.closest(".active-facets a, .active-facets__clear a, .mobile-facets__clear");
            if (!a) return;
            if (!a.href) return;

            // allow other links
            if (!a.href.includes("?")) return;

            e.preventDefault();
            const params = (a.href.split("?")[1] || "").trim();
            const ff = document.querySelector("facet-filters-form");
            if (ff?.onActiveFilterClick) ff.onActiveFilterClick(params);
            else window.location.href = a.href;
        });

        // popstate (back/forward)
        window.addEventListener("popstate", (event) => {
            const params = event.state?.searchParams ?? window.location.search.replace(/^\?/, "");
            if (params !== STATE.params) render(params, { updateHistory: false, restoreDropdowns: true });
        });
    };

    /* -----------------------------
      Sort overlay wiring (your custom overlay)
    ----------------------------- */
    const bindSortOverlay = () => {
        const overlay = document.querySelector(SEL.sortOverlay);
        if (!overlay) return;

        if (overlay.__bound) return;
        overlay.__bound = true;

        const select = document.querySelector(SEL.sortSelect);
        const closeBtn = document.querySelector(SEL.sortCloseButton);
        const openBtn = document.querySelector(SEL.sortOpenButton);

        const mobileOnly = window.matchMedia("(max-width: 749px)");

        const open = () => {
            if (!mobileOnly.matches) return;
            // kfb pill bar handles mobile sort — skip old overlay
            if (document.getElementById("kfbWrap")) return;
            overlay.style.display = "flex";
            requestAnimationFrame(() => overlay.classList.add("visible"));
            document.body.style.overflow = "hidden";
        };

        const close = () => {
            overlay.classList.remove("visible");
            setTimeout(() => {
                overlay.style.display = "none";
                document.body.style.overflow = "";
            }, 250);
        };

        openBtn?.addEventListener("click", (e) => {
            e.preventDefault();
            open();
        });
        closeBtn?.addEventListener("click", (e) => {
            e.preventDefault();
            close();
        });

        overlay.addEventListener("click", (e) => {
            // click outside inner panel closes
            if (e.target === overlay) close();
        });

        overlay.querySelectorAll(SEL.sortOption).forEach((opt) => {
            opt.addEventListener("click", (e) => {
                e.preventDefault();
                if (!mobileOnly.matches) return;

                const value = opt.dataset.value;
                if (!value) return;

                // update select (Dawn compatible)
                if (select) select.value = value;

                // highlight
                overlay.querySelectorAll(SEL.sortOption).forEach((o) => o.classList.remove("selected"));
                opt.classList.add("selected");

                // run facets render using merged params
                render(collectParams(), { updateHistory: true });
                close();
            });
        });
    };

    /* -----------------------------
      Boot (define + initial bindings)
    ----------------------------- */
    const defineSafely = (name, klass) => {
        if (!customElements.get(name)) customElements.define(name, klass);
    };

    safe(() => {
        defineSafely("facet-filters-form", FacetFiltersForm);
        defineSafely("facet-remove", FacetRemove);

        bindDelegatesOnce();
        bindSortOverlay();
        initPriceSliders();
        window.KYMEE_FACETS = {
            render,
            collectParams
        };

        // After every update, re-bind overlay + re-init price sliders (DOM replaced)
        document.addEventListener("collection:updated", () => {
            requestIdleCallback(bindSortOverlay, { timeout: 300 });
            requestIdleCallback(initPriceSliders, { timeout: 300 });
        });

        // Startup scroll diagnostics for facets wrapper (CSS sticky)
        const logFacetsState = (label) => {
            const fw = document.querySelector('.facets-wrapper');
            if (!fw) return;
            const rect = fw.getBoundingClientRect();
            const style = window.getComputedStyle(fw);
        };

        logFacetsState('at boot');
        requestAnimationFrame(() => logFacetsState('RAF 1'));
        requestAnimationFrame(() => requestAnimationFrame(() => logFacetsState('RAF 2')));

        window.addEventListener('pageshow', () => {
            requestAnimationFrame(() => logFacetsState('pageshow+RAF'));
        }, { once: true });
    });
})();


/* =======================================================
   ✅ FINAL STICKY FACETS — SUBCATEGORY DRIVEN (NO GAP)
   Header & Subcategory handled by StickyHeader ONLY
========================================================== */
(() => {
    const facets = document.querySelector(".facets-wrapper");
    const subcategory = document.querySelector(".subcategory-navigation");
    const header = document.querySelector(".section-header");

    // Guard: this sticky logic only applies on pages that have ALL three elements
    // (e.g. collection pages). Search / other pages won't have subcategory nav.
    if (!facets || !subcategory || !header) return;

    const subcategoryNav = subcategory.querySelector(".subcategory-navigation__inner");

    let isDesktop = window.matchMedia("(min-width: 750px)").matches;
    let ticking = false;

    // 🔒 Capture document position ONCE
    const subcategoryDocTop =
        subcategory.getBoundingClientRect().top + window.scrollY;

    const updateSticky = () => {
        if (!isDesktop) return;

        const scrollTop = window.scrollY || window.pageYOffset;

        let triggerPoint = 0;
        let top = 0;


        // ✅ CONDITION 1: subcategory INNER EXISTS
        if (subcategoryNav !== null && subcategoryNav.offsetHeight > 0) {

            // Trigger after subcategory finishes
            const subDocTop =
                subcategory.offsetTop + subcategory.offsetHeight;

            triggerPoint = subDocTop;

            // Top = header + full subcategory
            top = header.offsetHeight + subcategory.offsetHeight;

        }
        // ✅ CONDITION 2: NO inner nav → header only
        else {

            triggerPoint =
                header.offsetTop;

            top = header.offsetHeight;
        }

        if (scrollTop >= triggerPoint) {
            facets.classList.add("is-fixed"); // use custom class
            facets.style.top = `${top - 1}px`;
        } else {
            facets.classList.remove("is-fixed");
            facets.style.top = "";
        }
    };



    const rafScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
            updateSticky();
            ticking = false;
        });
    };

    window.addEventListener("scroll", rafScroll, { passive: true });
    window.addEventListener("resize", () => {
        isDesktop = window.matchMedia("(min-width: 750px)").matches;
        updateSticky();
    });

    // 🔥 After AJAX filter update
    document.addEventListener("collection:updated", () => {
        requestAnimationFrame(updateSticky);
    });

    // initial
    requestAnimationFrame(updateSticky);
})();


